import { Badge } from '@/components/ui/badge';
import type { Empresa } from '@/api/types';
import { cn } from '@/lib/cn';
import {
  Globe,
  Instagram,
  Linkedin,
  Youtube,
  Facebook,
  Twitter,
  ExternalLink,
  Copy,
  Check,
  Building2,
  Sparkles,
  Phone,
  MessageCircle,
} from 'lucide-react';
import { cleanPhone, formatPhone, whatsappUrl } from '@/lib/phone';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useEmpresaUpdate } from '@/api/mutations/empresa';
import { InlineEdit, InlineTextarea, InlineTags, InlineRedes } from './InlineEdit';
import { EnrichModal } from './EnrichModal';
import { BadgeSelect } from './BadgeSelect';
import { DeleteButton } from './DeleteButton';
import { displayName, secondaryName } from '@/lib/empresa';

const tempPillColor: Record<string, string> = {
  frio: 'bg-[oklch(0.94_0.012_240)] text-[oklch(0.42_0.04_240)] dark:bg-[oklch(0.26_0.015_240)] dark:text-[oklch(0.78_0.04_240)]',
  morno:
    'bg-[oklch(0.94_0.05_60)] text-[oklch(0.42_0.13_50)] dark:bg-[oklch(0.28_0.05_50)] dark:text-[oklch(0.82_0.13_60)]',
  quente:
    'bg-[oklch(0.93_0.06_28)] text-[oklch(0.45_0.16_28)] dark:bg-[oklch(0.28_0.06_28)] dark:text-[oklch(0.82_0.16_28)]',
};

const STATUS_FUNIL_OPTIONS = [
  { value: 'lista', label: 'lista' },
  { value: 'abertura', label: 'abertura' },
  { value: 'qualificacao', label: 'qualificação' },
  { value: 'call_diagnostico', label: 'call diagnóstico' },
  { value: 'diagnostico_pago', label: 'diagnóstico pago' },
  { value: 'proposta', label: 'proposta' },
  { value: 'objecao', label: 'objeção' },
  { value: 'fechamento', label: 'fechamento' },
  { value: 'onboarding', label: 'onboarding' },
  { value: 'retencao', label: 'retenção' },
  { value: 'perdido', label: 'perdido' },
  { value: 'dead_onb', label: 'dead onb' },
];

const PRIORIDADE_OPTIONS = [
  { value: 'alta', label: 'alta' },
  { value: 'media', label: 'média' },
  { value: 'baixa', label: 'baixa' },
];

const TEMPERATURA_OPTIONS = [
  { value: 'frio', label: 'frio' },
  { value: 'morno', label: 'morno' },
  { value: 'quente', label: 'quente' },
];

const PULSO_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  label: `pulso ${n}/5`,
}));

const REDE_ICONS: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  tiktok: Globe,
};

function ensureUrl(value: string): string {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

function formatCnpj(cnpj: string): string {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const Icon = copied ? Check : Copy;
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Não foi possível copiar');
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
      aria-label={`Copiar ${label}`}
    >
      <Icon className="h-3 w-3" strokeWidth={2.25} />
    </button>
  );
}

function ChannelLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-0.5 text-[11px] text-foreground/85 transition-colors hover:border-border hover:bg-muted"
      title={href}
    >
      <Icon className="h-3 w-3" strokeWidth={2.25} />
      <span>{label}</span>
    </a>
  );
}

function buildDossieUrls(e: Empresa): string[] {
  const urls: string[] = [];
  if (e.site) urls.push(ensureUrl(e.site));
  if (e.redes?.instagram) urls.push(ensureUrl(e.redes.instagram));
  if (e.redes?.linkedin) urls.push(ensureUrl(e.redes.linkedin));
  const mapsQuery = [e.nome, e.cidade, e.estado].filter(Boolean).join(' ');
  urls.push(`https://www.google.com/maps/search/${encodeURIComponent(mapsQuery)}`);
  return urls;
}

function openDossie(e: Empresa) {
  const urls = buildDossieUrls(e);
  urls.forEach((url, i) => {
    setTimeout(() => window.open(url, '_blank', 'noopener,noreferrer'), i * 100);
  });
  toast.success(`Abrindo ${urls.length} abas`);
}

export function EmpresaProfileHeader({ e }: { e: Empresa }) {
  const update = useEmpresaUpdate();
  const nav = useNavigate();
  const [enrichOpen, setEnrichOpen] = useState(false);
  const redes = e.redes ?? {};

  async function patch(payload: Partial<Empresa> & { redes?: Record<string, string> }) {
    try {
      await update.mutateAsync({ id: e.id, payload });
      toast.success('salvo');
    } catch (err) {
      toast.error('Falha ao salvar: ' + (err instanceof Error ? err.message : 'erro'));
      throw err;
    }
  }

  const hasCanais =
    Boolean(e.site) || Object.keys(redes).length > 0 || Boolean(e.cnpj);

  return (
    <div className="space-y-5 border-b border-border/70 pb-5">
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-[32px] font-semibold leading-[1.05] tracking-[-0.025em]">
            {displayName(e)}
          </h1>
          {secondaryName(e) && (
            <p className="mt-1 text-sm text-muted-foreground">{secondaryName(e)}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <DeleteButton entity="empresa" id={e.id} onDeleted={() => nav('/empresas')} iconOnly />
          <button
            type="button"
            onClick={() => setEnrichOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2.5 py-1.5 text-xs font-medium text-foreground/85 transition-colors hover:bg-muted"
            title="Buscar dados via URL do site ou CNPJ"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.25} />
            enriquecer
          </button>
          <button
            type="button"
            onClick={() => openDossie(e)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            title="Abre site, redes e Google Maps em novas abas"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.25} />
            abrir dossiê
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        <span className="lowercase">{e.setor}</span>
        {e.cidade && <span>· {e.cidade}</span>}
        {e.estado && <span className="uppercase mono text-[11px]">{e.estado}</span>}
        <span className="text-muted-foreground/50">·</span>
        <span className="italic">
          via{' '}
          <InlineEdit
            value={e.fonte_descoberta}
            onSave={(v) => patch({ fonte_descoberta: v })}
            placeholder="indicação, instagram, pesquisa"
            emptyLabel="?"
          />
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <BadgeSelect
          value={e.temperatura_efetiva}
          options={TEMPERATURA_OPTIONS}
          onSelect={(v) => patch({ temperatura_efetiva: v as Empresa['temperatura_efetiva'] })}
          className={cn('rounded-full', tempPillColor[e.temperatura_efetiva])}
        />
        <BadgeSelect
          value={e.status_funil}
          options={STATUS_FUNIL_OPTIONS}
          onSelect={(v) => patch({ status_funil: v as Empresa['status_funil'] })}
        />
        <BadgeSelect
          value={e.prioridade}
          options={PRIORIDADE_OPTIONS}
          onSelect={(v) => patch({ prioridade: v as Empresa['prioridade'] })}
        />
        <BadgeSelect
          value={String(e.pulso_digital ?? 3)}
          options={PULSO_OPTIONS}
          onSelect={(v) => patch({ pulso_digital: parseInt(v, 10) })}
        />
        {e.status_site && (
          <Badge
            variant={e.status_site === 'online' ? 'secondary' : 'destructive'}
            className="lowercase"
          >
            site {e.status_site}
          </Badge>
        )}
      </div>

      {(hasCanais || true) && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {e.site && <ChannelLink href={ensureUrl(e.site)} label="site" icon={Globe} />}
            {Object.entries(redes).map(([rede, url]) => {
              if (!url) return null;
              const Icon = REDE_ICONS[rede.toLowerCase()] ?? Globe;
              return <ChannelLink key={rede} href={ensureUrl(url)} label={rede} icon={Icon} />;
            })}
            {e.cnpj && (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-0.5 text-[11px]">
                <Building2 className="h-3 w-3 text-muted-foreground" strokeWidth={2.25} />
                <span className="mono text-foreground/85">{formatCnpj(e.cnpj)}</span>
                <CopyButton value={formatCnpj(e.cnpj)} label="CNPJ" />
              </div>
            )}
            {e.telefone_receita && (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-50 px-2.5 py-0.5 text-[11px] dark:bg-emerald-950/50">
                <Phone className="h-3 w-3 text-emerald-700 dark:text-emerald-400" strokeWidth={2.25} />
                <a
                  href={`tel:${cleanPhone(e.telefone_receita)}`}
                  className="mono text-emerald-900 hover:underline dark:text-emerald-200"
                >
                  {formatPhone(e.telefone_receita)}
                </a>
                <a
                  href={whatsappUrl(e.telefone_receita)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:opacity-80"
                  title="Abrir WhatsApp"
                >
                  <MessageCircle className="h-3 w-3" strokeWidth={2.25} />
                </a>
                <CopyButton value={formatPhone(e.telefone_receita)} label="Telefone" />
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70">receita</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Globe className="h-3 w-3" strokeWidth={2.25} />
              <InlineEdit
                value={e.site}
                onSave={(v) => patch({ site: v })}
                placeholder="empresa.com.br"
                emptyLabel="+ site"
                type="url"
              />
            </span>
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3 w-3" strokeWidth={2.25} />
              <InlineEdit
                value={e.cnpj}
                onSave={(v) => patch({ cnpj: v.replace(/\D/g, '') })}
                placeholder="00.000.000/0001-00"
                emptyLabel="+ cnpj"
                display={(v) => <span className="mono">{formatCnpj(v)}</span>}
              />
            </span>
            <InlineRedes value={e.redes} onSave={(redes) => patch({ redes })} />
          </div>
        </div>
      )}

      <div className="pt-1">
        <InlineTags value={e.tags} onSave={(tags) => patch({ tags })} />
      </div>

      <InlineTextarea
        value={e.notas_livres}
        onSave={(v) => patch({ notas_livres: v })}
        placeholder="Notas sobre essa empresa, contexto, observações..."
        emptyLabel="+ notas"
      />

      <EnrichModal
        empresaId={e.id}
        empresaNome={e.nome}
        open={enrichOpen}
        onOpenChange={setEnrichOpen}
        defaultUrl={e.site}
        defaultCnpj={e.cnpj}
      />
    </div>
  );
}
